import { useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  MessageSquare, 
  Send,
  Download,
  Clock,
  FileText,
  Image as ImageIcon,
  Video,
  File
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { getProjectByToken } from '../../services/projectService'
import { getProjectVersions, getFileUrl } from '../../services/fileService'
import { 
  createComment, 
  approveVersion, 
  requestChanges,
  getVersionComments 
} from '../../services/approvalService'

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  in_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  changes_requested: 'bg-yellow-100 text-yellow-800'
}

const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return ImageIcon
  if (fileType?.startsWith('video/')) return Video
  return File
}

export default function ClientReview() {
  const { token } = useParams()
  const [project, setProject] = useState(null)
  const [versions, setVersions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [commentText, setCommentText] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [filePreviewUrl, setFilePreviewUrl] = useState(null)

  useEffect(() => {
    loadProject()
  }, [token])

  useEffect(() => {
    if (selectedVersion) {
      loadComments()
      loadFilePreview()
    }
  }, [selectedVersion])

  const loadProject = async () => {
    try {
      setLoading(true)
      const data = await getProjectByToken(token)
      setProject(data)

      // Load versions
      const versionsData = await getProjectVersions(data.id)
      setVersions(versionsData || [])
      
      // Select latest version
      if (versionsData && versionsData.length > 0) {
        setSelectedVersion(versionsData[0])
      }
    } catch (err) {
      setError(err.message || 'Invalid or expired access link')
    } finally {
      setLoading(false)
    }
  }

  const loadComments = async () => {
    if (!selectedVersion) return
    
    try {
      const { data } = await getVersionComments(selectedVersion.id)
      setComments(data || [])
    } catch (err) {
      console.error('Error loading comments:', err)
    }
  }

  const loadFilePreview = async () => {
    if (!selectedVersion) return
    
    try {
      const url = await getFileUrl(selectedVersion.file_path, 3600)
      setFilePreviewUrl(url)
    } catch (err) {
      console.error('Error loading file preview:', err)
    }
  }

  const handleAddComment = async (e) => {
    e.preventDefault()
    
    if (!commentText.trim() || !clientEmail.trim()) {
      alert('Please enter your email and comment')
      return
    }

    try {
      setSubmitting(true)
      
      await createComment({
        version_id: selectedVersion.id,
        content: commentText,
        client_email: clientEmail,
        metadata: {
          ip_address: await getUserIP(),
          user_agent: navigator.userAgent
        }
      })

      setCommentText('')
      await loadComments()
      alert('Comment added successfully!')
    } catch (error) {
      console.error('Error adding comment:', error)
      alert('Failed to add comment')
    } finally {
      setSubmitting(false)
    }
  }

  const handleApprove = async () => {
    if (!clientEmail.trim()) {
      alert('Please enter your email address')
      return
    }

    if (!confirm('Are you sure you want to approve this version?')) {
      return
    }

    try {
      setSubmitting(true)
      
      await approveVersion(
        selectedVersion.id,
        null, // No user_id for client approvals
        commentText || 'Approved',
        {
          client_email: clientEmail,
          ip_address: await getUserIP(),
          user_agent: navigator.userAgent
        }
      )

      setCommentText('')
      await loadProject()
      await loadComments()
      alert('Version approved successfully! The team has been notified.')
    } catch (error) {
      console.error('Error approving:', error)
      alert('Failed to approve: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleRequestChanges = async () => {
    if (!clientEmail.trim() || !commentText.trim()) {
      alert('Please enter your email and describe the changes needed')
      return
    }

    if (!confirm('Request changes to this version?')) {
      return
    }

    try {
      setSubmitting(true)
      
      await requestChanges(
        selectedVersion.id,
        null,
        commentText,
        {
          client_email: clientEmail,
          ip_address: await getUserIP(),
          user_agent: navigator.userAgent
        }
      )

      setCommentText('')
      await loadProject()
      await loadComments()
      alert('Change request submitted successfully!')
    } catch (error) {
      console.error('Error requesting changes:', error)
      alert('Failed to request changes: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip
    } catch {
      return 'unknown'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
        <div className="max-w-md w-full">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800 mb-1">Access Denied</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!selectedVersion && versions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Versions Available</h3>
          <p className="text-gray-600">This project doesn't have any versions yet.</p>
        </div>
      </div>
    )
  }

  const FileIcon = getFileIcon(selectedVersion?.file_type)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{project.title}</h1>
              <p className="text-gray-600">{project.description}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${statusColors[project.status]}`}>
              {project.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Version Selector */}
            {versions.length > 1 && (
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
                <label className="block text-sm font-medium mb-2">Select Version</label>
                <select
                  value={selectedVersion?.id || ''}
                  onChange={(e) => {
                    const version = versions.find(v => v.id === e.target.value)
                    setSelectedVersion(version)
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                >
                  {versions.map(version => (
                    <option key={version.id} value={version.id}>
                      Version {version.version_number} - {version.file_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* File Preview */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FileIcon className="w-5 h-5 text-gray-600" />
                  <div>
                    <h3 className="font-semibold">Version {selectedVersion.version_number}</h3>
                    <p className="text-sm text-gray-600">{selectedVersion.file_name}</p>
                  </div>
                </div>
                <a
                  href={filePreviewUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              </div>

              {/* Preview Area */}
              <div className="bg-gray-100  rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
                {selectedVersion.file_type?.startsWith('image/') && filePreviewUrl && (
                  <img 
                    src={filePreviewUrl} 
                    alt={selectedVersion.file_name}
                    className="w-full h-full object-contain"
                  />
                )}
                {selectedVersion.file_type?.startsWith('video/') && filePreviewUrl && (
                  <video 
                    src={filePreviewUrl} 
                    controls
                    className="w-full h-full"
                  />
                )}
                {selectedVersion.file_type === 'application/pdf' && filePreviewUrl && (
                  <iframe 
                    src={filePreviewUrl} 
                    className="w-full h-full"
                    style={{ minHeight: '600px' }}
                    title="PDF Preview"
                  />
                )}
              </div>

              <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date(selectedVersion.created_at).toLocaleDateString()}
                </span>
                <span>
                  {(selectedVersion.file_size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            </div>

            {/* Comments */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Comments ({comments.length})
              </h3>

              <div className="space-y-3 mb-4">
                {comments.map(comment => (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-medium text-sm">
                        {comment.client_email || comment.user?.email || 'Anonymous'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700">{comment.content}</p>
                  </div>
                ))}

                {comments.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No comments yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Actions */}
          <div className="space-y-6">
            {/* Client Info */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h3 className="font-semibold mb-4">Your Information</h3>
              <div>
                <label className="block text-sm font-medium mb-2">Email Address*</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>
            </div>

            {/* Feedback */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h3 className="font-semibold mb-4">Your Feedback</h3>
              <form onSubmit={handleAddComment}>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add your comments or feedback..."
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black mb-3"
                />
                <button
                  type="submit"
                  disabled={submitting || !commentText.trim() || !clientEmail.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Add Comment
                </button>
              </form>
            </div>

            {/* Decision */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h3 className="font-semibold mb-4">Decision</h3>
              <div className="space-y-3">
                <button
                  onClick={handleApprove}
                  disabled={submitting || !clientEmail.trim()}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approve
                </button>
                <button
                  onClick={handleRequestChanges}
                  disabled={submitting || !clientEmail.trim() || !commentText.trim()}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <XCircle className="w-5 h-5" />
                  Request Changes
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {!clientEmail.trim() && 'Please enter your email to continue'}
                {clientEmail.trim() && !commentText.trim() && 'Add feedback before requesting changes'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
